import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import './ElementBackground.css';

const THEME_LIGHT = 'light';
const THEME_DARK = 'dark';
const PANEL_COLOR_VAR = '--panel-color';

// Theme-aware gradient tuning
const GRADIENT_THEME = {
    [THEME_DARK]: {
        dimFactor: 0.18,
        minChannel: 10,
        startOpacityMultiplier: 1,
    },
    [THEME_LIGHT]: {
        dimFactor: 0.9,
        minChannel: 40,
        startOpacityMultiplier: 1,
    },
};
// Linear gradient configuration
const LINEAR_CONFIG = {
    MAX_INTENSITY: 1,
    START_OPACITY: 0.2,
    GRADIENT_RADIUS: '40rem'
};

// Animation configuration
const ANIMATION_CONFIG = {
    TRANSITION_DURATION: '0.5s',
    TRANSITION_EASING: 'ease-in-out',
    ENABLE_ANIMATIONS: true
};

// Gradient positioning
const GRADIENT_CONFIG = {
    CENTER_X: '50%',
    CENTER_Y: '33%',
    TYPE: 'radial-gradient'
};

const parseRgbString = (value) => {
    if (!value) return null;
    const rgbMatch = value.match(/rgba?\s*\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
    if (rgbMatch) {
        return {
            r: parseInt(rgbMatch[1], 10),
            g: parseInt(rgbMatch[2], 10),
            b: parseInt(rgbMatch[3], 10),
        };
    }
    const hexMatch = value.trim().match(/^#([0-9a-f]{6})$/i);
    if (hexMatch) {
        const hex = hexMatch[1];
        return {
            r: parseInt(hex.slice(0, 2), 16),
            g: parseInt(hex.slice(2, 4), 16),
            b: parseInt(hex.slice(4, 6), 16),
        };
    }
    return null;
};

let cachedPanelColor = null;
let cachedPanelThemeKey = null;

const getCurrentThemeKey = () => {
    if (typeof document === 'undefined') {
        return THEME_DARK;
    }
    const themeAttr = document.body?.getAttribute('data-theme');
    return themeAttr === THEME_LIGHT ? THEME_LIGHT : THEME_DARK;
};

const readPanelColorFromDom = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return null;
    }
    const body = document.body;
    const root = document.documentElement;
    const rawBody = body ? window.getComputedStyle(body).getPropertyValue(PANEL_COLOR_VAR) : '';
    const parsedBody = parseRgbString(rawBody);
    if (parsedBody) {
        return parsedBody;
    }
    const rawRoot = root ? window.getComputedStyle(root).getPropertyValue(PANEL_COLOR_VAR) : '';
    return parseRgbString(rawRoot);
};

const getPanelColor = () => {
    const themeKey = getCurrentThemeKey();
    if (cachedPanelColor && cachedPanelThemeKey === themeKey) {
        return cachedPanelColor;
    }
    cachedPanelColor = readPanelColorFromDom();
    cachedPanelThemeKey = themeKey;
    return cachedPanelColor;
};

const invalidatePanelColorCache = () => {
    cachedPanelColor = null;
    cachedPanelThemeKey = null;
};

const getGradientTheme = () => {
    const key = getCurrentThemeKey();
    return GRADIENT_THEME[key] || GRADIENT_THEME[THEME_DARK];
};

const getStartOpacity = () => {
    const { startOpacityMultiplier } = getGradientTheme();
    return LINEAR_CONFIG.START_OPACITY * startOpacityMultiplier;
};

const ElementBackground = ({ build, flatColor, direction = "180deg" }) => {
    const [currentGradient, setCurrentGradient] = useState('');
    const [themeVersion, setThemeVersion] = useState(0);
    const previousColorsRef = useRef({ r: 0, g: 0, b: 0 });
    const animationFrameRef = useRef(null);
    const animationStartTimeRef = useRef(0);

    // Helper function to interpolate between two colors
    const interpolateColors = (color1, color2, progress) => {
        return {
            r: Math.round(color1.r + (color2.r - color1.r) * progress),
            g: Math.round(color1.g + (color2.g - color1.g) * progress),
            b: Math.round(color1.b + (color2.b - color1.b) * progress)
        };
    };

    const dimmedEndColor = (r, g, b) => {
        const { dimFactor, minChannel } = getGradientTheme();
        const panel = getPanelColor();
        const isLight = getCurrentThemeKey() === THEME_LIGHT;

        const floor = isLight && panel ? panel : { r: minChannel, g: minChannel, b: minChannel };
        const base = {
            r: Math.max(floor.r, Math.round(r * dimFactor)),
            g: Math.max(floor.g, Math.round(g * dimFactor)),
            b: Math.max(floor.b, Math.round(b * dimFactor))
        };
        if (!panel) {
            return base;
        }
        return {
            r: Math.round((base.r + panel.r) / 2),
            g: Math.round((base.g + panel.g) / 2),
            b: Math.round((base.b + panel.b) / 2)
        };
    };

    const createGradientString = (r, g, b) => {
        const startOpacity = getStartOpacity();
        const startRgba = `rgba(${r}, ${g}, ${b}, ${startOpacity})`;
        const end = dimmedEndColor(r, g, b);
        const endRgba = `rgba(${end.r}, ${end.g}, ${end.b}, 1)`;

        return `${GRADIENT_CONFIG.TYPE}(circle at ${GRADIENT_CONFIG.CENTER_X} ${GRADIENT_CONFIG.CENTER_Y}, ${startRgba} 0%, ${endRgba} ${LINEAR_CONFIG.GRADIENT_RADIUS})`;
    };

    // Animation function
    const animateColorTransition = (fromColors, toColors) => {
        if (!ANIMATION_CONFIG.ENABLE_ANIMATIONS) {
            setCurrentGradient(createGradientString(toColors.r, toColors.g, toColors.b));
            return;
        }

        animationStartTimeRef.current = performance.now();

        const animate = (currentTime) => {
            const elapsed = currentTime - animationStartTimeRef.current;
            const duration = parseFloat(ANIMATION_CONFIG.TRANSITION_DURATION) * 1000;
            const progress = Math.min(elapsed / duration, 1);

            // Apply easing
            let easedProgress;
            switch (ANIMATION_CONFIG.TRANSITION_EASING) {
                case 'ease-out':
                    easedProgress = 1 - Math.pow(1 - progress, 3);
                    break;
                case 'ease-in':
                    easedProgress = Math.pow(progress, 3);
                    break;
                case 'ease-in-out':
                    easedProgress = progress < 0.5 ?
                        4 * progress * progress * progress :
                        1 - Math.pow(-2 * progress + 2, 3) / 2;
                    break;
                default:
                    easedProgress = progress;
            }

            const interpolatedColors = interpolateColors(fromColors, toColors, easedProgress);
            setCurrentGradient(createGradientString(interpolatedColors.r, interpolatedColors.g, interpolatedColors.b));

            if (progress < 1) {
                animationFrameRef.current = requestAnimationFrame(animate);
            } else {
                previousColorsRef.current = toColors;
            }
        };

        animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Cleanup animation on unmount
    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    // React to theme changes (light/dark) by bumping a version counter
    useEffect(() => {
        if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') {
            return;
        }
        const target = document.body;
        if (!target) {
            return;
        }
        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
                    invalidatePanelColorCache();
                    setThemeVersion((v) => v + 1);
                    break;
                }
            }
        });
        observer.observe(target, { attributes: true, attributeFilter: ['data-theme'] });
        return () => observer.disconnect();
    }, []);

    // Trigger animation when build changes
    useEffect(() => {
        // In flat-color mode the gradient is derived on every render via
        // generateFlatColor(); the animated `currentGradient` is unused, so
        // skip the rAF machinery entirely (cards mount many of these).
        if (flatColor) return;
        if (!build) return;

        const newColors = build.getElementTint();

        // If this is the first render or animations are disabled, set immediately
        if (!ANIMATION_CONFIG.ENABLE_ANIMATIONS ||
            (previousColorsRef.current.r === 0 && previousColorsRef.current.g === 0 && previousColorsRef.current.b === 0)) {
            setCurrentGradient(createGradientString(newColors.r, newColors.g, newColors.b));
            previousColorsRef.current = newColors;
            return;
        }

        // Animate from previous colors to new colors
        animateColorTransition(previousColorsRef.current, newColors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [build, themeVersion]); // Recompute when build or theme changes

    // Generate blended linear gradient
    const generateBlendedGradient = () => {
        if (!build) return '';

        const { r, g, b } = build.getElementTint();

        const startOpacity = getStartOpacity();
        const end = dimmedEndColor(r, g, b);

        const startRgba = `rgba(${r}, ${g}, ${b}, ${startOpacity})`;
        const endRgba = `rgba(${end.r}, ${end.g}, ${end.b}, 1)`;

        return `${GRADIENT_CONFIG.TYPE}(circle at ${GRADIENT_CONFIG.CENTER_X} ${GRADIENT_CONFIG.CENTER_Y}, ${startRgba} 0%, ${endRgba} ${LINEAR_CONFIG.GRADIENT_RADIUS})`;
    };

    // Generate flat color based on prop
    const generateFlatColor = () => {
        if (!build) {
            const fallback = dimmedEndColor(200, 200, 200);
            return `rgba(${fallback.r}, ${fallback.g}, ${fallback.b}, 1)`;
        }

        // The flat path is the build-card path, and cards are served without
        // equipment to keep the listing small. getElementTint() computes from
        // stats when they are there and reads the stored tint when they are not,
        // so this works either way — a plain getStats() here would flatten every
        // community card to grey.
        const { r, g, b, elementTotal } = build.getElementTint();
        const intensity = elementTotal * LINEAR_CONFIG.MAX_INTENSITY;

        if (flatColor === 'start') {
            const opacity = getStartOpacity() * intensity;
            return `rgba(${r}, ${g}, ${b}, ${opacity})`;
        } else if (flatColor === 'end') {
            const end = dimmedEndColor(r, g, b);
            return `rgba(${end.r}, ${end.g}, ${end.b}, 1)`;
        } else {
            // Default to start opacity if invalid value
            const opacity = getStartOpacity() * intensity;
            return `rgba(${r}, ${g}, ${b}, ${opacity})`;
        }
    };

    const backgroundStyle = flatColor ?
        {
            backgroundColor: generateFlatColor()
        } :
        {
            background: currentGradient || generateBlendedGradient()
        };

    return <div className="element-background" style={backgroundStyle} />;
};

ElementBackground.propTypes = {
    build: PropTypes.object,
    flatColor: PropTypes.oneOf(['start', 'end']),
    direction: PropTypes.string
};

export default ElementBackground; 
